import { createTheme, type Theme } from '@mui/material'
import { ThemeMode, TrabaTheme } from '../types/theme'

interface TrabaThemeOptions {
  ActiveBackColor?: string
  ReverseBackColor?: string
  TextButton?: string
  ActiveHoverColor?: string
  ReverseHoverColor?: string
  BorderColor?: string
  HoverBorderColor?: string
  HoverTextButton?: string
  error?: {
    border?: string
  }
  success?: {
    border?: string
  }
  default?: {
    fontSize?: string
  }
  border?: {
    radius?: string
    color?: string
  }
  colors?: {
    brand?: string
    reverseBackColor?: string
    textButton?: string
    red?: string
    Black?: string
    Violet10?: string
    Grey60?: string
    Grey50?: string
    Grey10?: string
    Grey80?: string
    Grey90?: string
    Blue100?: string
    Violet?: string
    TrabaBlue?: string
    MidnightBlue?: string
    Grey?: string
    Violet20: string
    Violet40: string
    Violet50: string
    Violet60: string
    Violet70: string
    Violet80: string
    Grey20: string
    Grey30: string
    Blue10: string
    Blue20: string
    Blue30: string
    Blue40: string
    Blue50: string
    Blue80: string
    Green10: string
    Green20: string
    Green30: string
    Green40: string
    Green50: string
    Green70: string
    Green80: string
    Yellow10: string
    Yellow60: string
    Yellow70: string
    Yellow80: string
    Orange10: string
    Orange60: string
    Orange70: string
    Red10: string
    Red20: string
    Red30: string
    Red40: string
    Red50: string
    Red60: string
    Red70: string
    Red80: string
    White: string
    Peach10: string
    Peach40: string
    Peach70: string
    Peach100: string
    Lime: string
    AIPurple: string
    AIPurpleLight: string
    AIGreenLight: string
    ScoreRed: string
    ScoreYellow: string
    ScoreTeal: string
  }
  shadow?: {
    xs?: string
    sm?: string
    md?: string
    lg?: string
    xl?: string
    spotlight?: string
    drawerRight?: string
    drawerLeft?: string
    brandGlow?: string
  }
  surface?: {
    primary?: string
    secondary?: string
    card?: string
    hover?: string
    overlay?: string
    disabled?: string
    subtleHover?: string
    glassTint?: string
    frosted?: string
    highlight?: string
  }
  media: {
    maxExtraSmall: string
    minSmall: string
    maxSmall: string
    minMedium: string
    maxMedium: string
    minLarge: string
    maxLarge: string
    minExtraLarge: string
  }
  space: {
    zero: number
    us: number
    ms: number
    xms: number
    xxs: number
    xxxs: number
    xsmed: number
    xs: number
    sm: number
    med: number
    lg: number
    xl: number
    xxl: number
    xxxl: number
  }
  palette?: {
    mode?: string
    primary?: {
      main?: string
      darker?: string
    }
    neutral?: {
      main?: string
    }
  }
}

declare module 'styled-components' {
  export interface DefaultTheme extends TrabaTheme {}
}

declare module '@mui/material/styles' {
  interface Theme extends TrabaTheme {}
  interface ThemeOptions extends TrabaThemeOptions {}

  interface Palette {
    neutral: Palette['primary']
  }
  interface PaletteOptions {
    neutral: PaletteOptions['primary']
  }
  interface PaletteColor {
    darker?: string
  }
  interface SimplePaletteColorOptions {
    darker?: string
  }
}

export const spacingPoints = ['us', 'xxs', 'xs', 'sm', 'med', 'lg', 'xl', 'xxl']

export const BrandColors = {
  Violet: '#8000FF',
  TrabaBlue: '#8793FF',
  MidnightBlue: '#08105E',
  Grey: '#E2E5E9',
  White: '#FFFFFF',
}

export const otherColors = {
  Violet10: '#F5EBFF',
  Violet20: '#E6CCFF',
  Violet30: '#D9B3FF',
  Violet40: '#B366FF',
  Violet50: '#9933FF',
  Violet60: '#8000FF',
  Violet70: '#6600CC',
  Violet80: '#4D0099',
  Violet90: '#330066',
  Grey10: '#F7F7F8',
  Grey20: '#E2E6E9',
  Grey30: '#C4CCD4',
  Grey40: '#B6BFC9',
  Grey50: '#7A8A99',
  Grey60: '#66788A',
  Grey80: '#414D58',
  Grey90: '#2B333B',
  Blue10: '#E9EFFC',
  Blue20: '#D3DFF9',
  Blue30: '#87CEFA',
  Blue40: '#3B82F6', // Bright blue for CTAs
  Blue50: '#4967A4',
  Blue80: '#133986',
  Blue100: '#08105E',
  Green10: '#EDF8F3',
  Green20: '#D9F1E6',
  Green30: '#B3E3D1',
  Green40: '#94D1B8',
  Green50: '#10B981', // Success/positive green
  Green70: '#1AB273',
  Green80: '#138656',
  Yellow10: '#FFF9E6',
  Yellow60: '#FFBF00',
  Yellow70: '#CC9900',
  Yellow80: '#997300',
  Orange10: '#FEF4E7',
  Orange60: '#F2930D',
  Orange70: '#C2750A',
  Red10: '#FEE7EE',
  Red20: '#FCCFDE',
  Red30: '#FA9EBD',
  Red40: '#F76E9C',
  Red50: '#EF4444', // Error/danger red
  Red60: '#F20D59',
  Red70: '#C20A47',
  Red80: '#910836',
  Peach10: '#FEEDE7',
  Peach40: '#F7906E',
  Peach70: '#C2380A',
  Peach100: '300E03',
  Lime: '#e8ff65',

  // AI accent colors (DSF/ShiftFill feature)
  AIPurple: '#9333EA',
  AIPurpleLight: '#A855F7',
  AIGreenLight: '#86EFAC',

  // Score gradient endpoints
  ScoreRed: '#DC2626',
  ScoreYellow: '#F59E0B',
  ScoreTeal: '#0D9488',
}

export const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 4px 16px rgba(0, 0, 0, 0.12)',
  xl: '0 8px 24px rgba(0, 0, 0, 0.12)',
  spotlight: '0 16px 70px rgba(0, 0, 0, 0.2)',
  drawerRight: '4px 0 12px rgba(0, 0, 0, 0.15)',
  drawerLeft: '-4px 0 20px rgba(0, 0, 0, 0.05)',
  brandGlow:
    '0 0 0 4px rgba(128, 0, 255, 0.18), 0 6px 16px -4px rgba(128, 0, 255, 0.6)',
}

export const surfaces = {
  primary: BrandColors.White,
  secondary: otherColors.Grey10,
  card: BrandColors.White,
  hover: BrandColors.White,
  overlay: 'rgba(0, 0, 0, 0.5)',
  disabled: 'rgba(128, 128, 128, 0.3)',
  subtleHover: 'rgba(0, 0, 0, 0.04)',
  glassTint: 'rgba(255, 255, 255, 0.3)',
  frosted: 'rgba(255, 255, 255, 0.85)',
  highlight: 'rgba(59, 130, 246, 0.2)',
}

/**
 * Dark mode color overrides.
 * Same token names, different values for dark backgrounds.
 */
const darkColors = {
  // White and Black stay as literal colors — use surface tokens for backgrounds
  Violet: '#B366FF',
  TrabaBlue: '#8793FF',
  MidnightBlue: '#E8ECEF',
  Grey: '#414D58',
  White: '#FFFFFF',

  Violet10: '#330066',
  Violet20: '#4D0099',
  Violet30: '#6600CC',
  Violet40: '#8000FF',
  Violet50: '#9933FF',
  Violet60: '#B366FF',
  Violet70: '#D9B3FF',
  Violet80: '#E6CCFF',
  Violet90: '#F5EBFF',

  Grey10: '#161b22',
  Grey20: '#30363d',
  Grey30: '#66788A',
  Grey40: '#7A8A99',
  Grey50: '#B6BFC9',
  Grey60: '#C4CCD4',
  Grey80: '#E2E6E9',
  Grey90: '#F7F7F8',

  Blue10: '#0D1B2B',
  Blue20: '#1A2E4D',
  Blue30: '#4967A4',
  Blue40: '#5B9BF7',
  Blue50: '#87CEFA',
  Blue80: '#5B9BF7',
  Blue100: '#E8ECEF',

  Green10: '#0D2B1F',
  Green20: '#0F3326',
  Green30: '#138656',
  Green40: '#1AB273',
  Green50: '#10B981',
  Green70: '#94D1B8',
  Green80: '#B3E3D1',

  Yellow10: '#2B2200',
  Yellow60: '#FFD040',
  Yellow70: '#FFBF00',
  Yellow80: '#CC9900',

  Orange10: '#2B1800',
  Orange60: '#F5A83D',
  Orange70: '#F2930D',

  Red10: '#2B0D17',
  Red20: '#3D1224',
  Red30: '#910836',
  Red40: '#C20A47',
  Red50: '#EF4444',
  Red60: '#F76E9C',
  Red70: '#FA9EBD',
  Red80: '#FCCFDE',

  Peach10: '#2B1207',
  Peach40: '#C2380A',
  Peach70: '#F7906E',
  Peach100: '#FEEDE7',

  Lime: '#e8ff65',

  AIPurple: '#A855F7',
  AIPurpleLight: '#C084FC',
  AIGreenLight: '#86EFAC',

  ScoreRed: '#EF4444',
  ScoreYellow: '#FBBF24',
  ScoreTeal: '#2DD4BF',
}

const darkShadows: typeof shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.3)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px rgba(0, 0, 0, 0.4)',
  lg: '0 4px 16px rgba(0, 0, 0, 0.5)',
  xl: '0 8px 24px rgba(0, 0, 0, 0.5)',
  spotlight: '0 16px 70px rgba(0, 0, 0, 0.6)',
  drawerRight: '4px 0 12px rgba(0, 0, 0, 0.5)',
  drawerLeft: '-4px 0 20px rgba(0, 0, 0, 0.3)',
  brandGlow:
    '0 0 0 4px rgba(179, 102, 255, 0.28), 0 6px 16px -4px rgba(179, 102, 255, 0.7)',
}

const darkSurfaces: typeof surfaces = {
  primary: '#0d1117',
  secondary: '#161b22',
  card: '#161b22',
  hover: '#21262d',
  overlay: 'rgba(0, 0, 0, 0.7)',
  disabled: 'rgba(128, 128, 128, 0.2)',
  subtleHover: 'rgba(255, 255, 255, 0.04)',
  glassTint: 'rgba(0, 0, 0, 0.3)',
  frosted: 'rgba(0, 0, 0, 0.85)',
  highlight: 'rgba(91, 155, 247, 0.2)',
}

export const MEDIA = {
  EXTRA_SMALL: 481,
  SMALL: 600,
  MEDIUM: 768,
  LARGE: 1131,
  EXTRA_LARGE: 1200,
}

/**
 * Transforms pure number amounts in strings with pixel unit added
 * E.g. 5 becomes "5px"
 * @param measure measure amount
 * @returns string value
 */
const SPACE = {
  zero: 0,
  us: 2,
  ms: 4,
  xxxs: 4,
  xms: 6,
  xxs: 8,
  xsmed: 12,
  xs: 16,
  sm: 24,
  med: 32,
  lg: 40,
  xl: 48,
  xxl: 56,
  xxxl: 64,
} as const

export function addPixelUnit(measure: string | number) {
  if (typeof measure === 'string') {
    if (spacingPoints.indexOf(measure) >= 0) {
      return `${SPACE[measure as keyof typeof SPACE]}px`
    }
    return measure
  }
  return `${measure}px`
}

/**
 * Creates a mode-aware Traba theme. Pass 'dark' for dark mode.
 * The static `theme` export below always uses light mode for backward compatibility.
 */
export function createTrabaTheme(mode: ThemeMode = 'light'): Theme {
  const isDark = mode === 'dark'
  const c = isDark
    ? { ...otherColors, ...BrandColors, ...darkColors }
    : { ...otherColors, ...BrandColors }
  const modeShadows = isDark ? darkShadows : shadows
  const modeSurfaces = isDark ? darkSurfaces : surfaces

  return createTheme({
    ActiveBackColor: c.Violet,
    ReverseBackColor: c.Violet10,
    ActiveHoverColor: c.Violet50,
    ReverseHoverColor: c.Violet20,
    TextButton: c.Grey60,
    HoverTextButton: c.MidnightBlue,
    BorderColor: c.Grey20,
    HoverBorderColor: c.Grey30,
    media: {
      maxExtraSmall: addPixelUnit(MEDIA.SMALL - 1),
      minSmall: addPixelUnit(MEDIA.SMALL),
      maxSmall: addPixelUnit(MEDIA.MEDIUM - 1),
      minMedium: addPixelUnit(MEDIA.MEDIUM),
      maxMedium: addPixelUnit(MEDIA.LARGE - 1),
      minLarge: addPixelUnit(MEDIA.LARGE),
      maxLarge: addPixelUnit(MEDIA.EXTRA_LARGE - 1),
      minExtraLarge: addPixelUnit(MEDIA.EXTRA_LARGE),
    },
    error: {
      border: c.Red60,
    },
    success: {
      border: c.Green70,
    },
    border: {
      radius: '8px',
      color: c.Grey20,
    },
    shadow: modeShadows,
    surface: modeSurfaces,
    colors: {
      brand: c.Violet,
      reverseBackColor: c.Violet10,
      textButton: c.Grey60,
      red: c.Red60,
      Black: '#000000',
      ...c,
    },
    default: {
      fontSize: '14px',
    },
    typography: {
      fontFamily: 'Poppins',
      allVariants: {
        fontFamily: 'Poppins',
      },
    },
    palette: {
      mode,
      primary: {
        main: c.Violet,
        darker: c.MidnightBlue,
      },
      neutral: {
        main: c.Grey60,
      },
      error: {
        main: c.Red60,
      },
      background: {
        default: modeSurfaces.primary,
        paper: modeSurfaces.card,
      },
      text: {
        primary: c.MidnightBlue,
        secondary: c.Grey60,
      },
    },
    shape: {
      borderRadius: 8,
    },
    space: SPACE,
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            padding: '7px',
            borderColor: c.Grey20,
            backgroundColor: modeSurfaces.card,
          },
          input: {
            padding: '7px',
            fontWeight: 300,
            fontSize: '13.333px',
          },
          notchedOutline: {
            borderColor: c.Grey20,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: 'Poppins',
            fontWeight: 300,
            fontSize: '14px',
            color: c.Grey50,

            '&.Mui-focused': {
              fontWeight: 500,
              fontSize: '16px',
            },
            '&.MuiInputLabel-shrink': {
              fontWeight: 500,
              fontSize: '16px',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            maxWidth: '400px',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderColor: c.Grey20,
            borderRadius: '8px',
          },
          input: {
            fontFamily: 'Poppins',
            fontWeight: 300,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${c.Grey20}`,
            backgroundColor: modeSurfaces.card,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: modeSurfaces.card,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            borderColor: c.Grey60,
            backgroundColor: c.Violet10,
            boxShadow: 'none',
            color: c.Violet,
            '&:hover': {
              backgroundColor: c.Violet20,
            },
          },
          outlined: {
            color: c.Grey60,
            borderColor: c.Grey,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: 14,
            color: c.MidnightBlue,
          },
        },
      },
    },
  }) as Theme
}

/** Light theme — backward-compatible static export */
export const theme = createTrabaTheme('light')

export const Z_INDEXES = {
  TABLE_HEADER: 2,
  DROPDOWN_LIST_CONTAINER: 3,
  FLOATING_BUTTON: 3,
  DRAWER: 4,
  MODAL: 1299, // Mui modal is 1200
  DATE_PICKER: 1300, // precedence over TABLE_HEADER and MODAL
  MODAL_POPPER: 1300,
  ALERT: 1399,
}

/**
 * Transition timing constants following Linear's performance philosophy:
 * - Shorter defaults than industry standard (Material: 200ms, iOS: 350ms)
 * - Asymmetric timing: instant show, delayed hide (feels snappier)
 *
 * @see https://x.com/brotzky/status/2057121845699354629
 */
export const TRANSITIONS = {
  /** Micro-interactions: button states, icon changes */
  instant: '0ms',
  /** Fast feedback: hover states, focus rings */
  fast: '80ms',
  /** Standard UI: tooltips, popovers, dropdowns */
  normal: '120ms',
  /** Deliberate: modals, drawers, page transitions */
  slow: '200ms',

  /** Easing functions */
  ease: {
    default: 'ease',
    out: 'ease-out',
    in: 'ease-in',
    inOut: 'ease-in-out',
  },
} as const

/**
 * Get asymmetric transition value (without 'transition:' prefix).
 * Use when composing with other transitions.
 *
 * @example
 * transition: top 120ms ease-out, ${getAsymmetricTransitionValue($visible, 'opacity')};
 */
export function getAsymmetricTransitionValue(
  isVisible: boolean,
  properties: string | string[] = 'all',
  hideDuration: string = TRANSITIONS.normal,
): string {
  const props = Array.isArray(properties) ? properties : [properties]
  const duration = isVisible ? TRANSITIONS.instant : hideDuration
  return props.map((p) => `${p} ${duration} ${TRANSITIONS.ease.out}`).join(', ')
}

/**
 * Asymmetric transition helper: instant appear, smooth disappear.
 * Use in styled-components with a $visible or $isOpen prop.
 * Returns full CSS property including 'transition:'.
 *
 * @example
 * const Tooltip = styled.div<{ $visible: boolean }>`
 *   opacity: ${({ $visible }) => ($visible ? 1 : 0)};
 *   ${({ $visible }) => asymmetricTransition($visible, 'opacity')}
 * `
 */
export function asymmetricTransition(
  isVisible: boolean,
  properties: string | string[] = 'all',
  hideDuration: string = TRANSITIONS.normal,
): string {
  return `transition: ${getAsymmetricTransitionValue(isVisible, properties, hideDuration)};`
}

/**
 * Pre-built asymmetric transition for common patterns.
 * Returns CSS string for styled-components (includes 'transition:' prefix).
 */
export const asymmetricTransitions = {
  opacity: (isVisible: boolean) => asymmetricTransition(isVisible, 'opacity'),
  transform: (isVisible: boolean) =>
    asymmetricTransition(isVisible, 'transform'),
  all: (isVisible: boolean) =>
    asymmetricTransition(isVisible, ['opacity', 'transform']),
  background: (isVisible: boolean) =>
    asymmetricTransition(isVisible, 'background-color'),
} as const
